import React, { useState, useEffect } from "react";
import { X, Search, Pin } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Lottie from "lottie-react";
import jiraIcon from "../../../../assets/icons/svg/setting/jira.svg";
import { ReactComponent as PinSelectedIcon } from "../../../../assets/icons/svg/Pin_selected.svg";
import { ReactComponent as PinUnSelectedIcon } from "../../../../assets/icons/svg/Pin_unselected.svg";
import CluvaJiraIntegration from "../../../../assets/icons/svg/setting/jira-integration.png";
import DotLoader from "../../../../assets/Animation/Dot Loader.json";
import Button from "../../../ui/buttons";
import {
  JiraIntegrationService,
  JiraProjectUser,
} from "../../../../services/JiraIntegrationService";
import { DEFAULT_FONT_FAMILY } from "../../../../config/fontConfig";

// LocalStorage key for pinned project
const PINNED_PROJECT_KEY = "jira_pinned_project_id";

// Jira API response structure based on actual API response
interface JiraProjectFromAPI {
  expand: string;
  self: string;
  id: string;
  key: string;
  name: string;
  avatarUrls: {
    "48x48": string;
    "24x24": string;
    "16x16": string;
    "32x32": string;
  };
  projectTypeKey: string;
  simplified: boolean;
  style: string;
  isPrivate: boolean;
  properties: Record<string, any>;
  entityId?: string;
  uuid?: string;
}

// Simplified interface for display
interface JiraProjectDisplay {
  id: string;
  key: string;
  name: string;
  avatarUrl: string;
}

interface JiraIntegrationPopupProps {
  isOpen: boolean;
  onClose: () => void;
  feedbackId?: string | number;
  onJiraActionComplete?: (result: {
    type: "success" | "error";
    title: string;
    message: string;
    jiraIssueKey?: string;
  }) => void;
}

// Project Avatar Component with proper image loading handling
const ProjectAvatar: React.FC<{ project: JiraProjectDisplay }> = ({
  project,
}) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  return (
    <div className="flex-shrink-0 relative w-7 h-7 3xl:w-8 3xl:h-8">
      {!imageError ? (
        <>
          <img
            src={project.avatarUrl}
            alt={project.name}
            className={`w-full h-full rounded object-cover transition-opacity duration-200 ${
              imageLoaded ? "opacity-100" : "opacity-0"
            }`}
            onLoad={() => setImageLoaded(true)}
            onError={() => {
              console.log(`Image failed to load for project: ${project.name}`);
              setImageError(true);
            }}
          />
          {!imageLoaded && (
            <div className="absolute inset-0 rounded bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center animate-pulse">
              <span className="text-white font-semibold text-sm 3xl:text-base">
                {project.key.charAt(0)}
              </span>
            </div>
          )}
        </>
      ) : (
        <div className="w-full h-full rounded bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center">
          <span className="text-white font-semibold text-sm 3xl:text-base">
            {project.key.charAt(0)}
          </span>
        </div>
      )}
    </div>
  );
};

const JiraIntegrationPopup: React.FC<JiraIntegrationPopupProps> = ({
  isOpen,
  onClose,
  feedbackId,
  onJiraActionComplete, // ✅ NEW: Receive callback from parent
}) => {
  const navigate = useNavigate();
  const [isAnimating, setIsAnimating] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [isCheckingConnection, setIsCheckingConnection] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [projects, setProjects] = useState<JiraProjectDisplay[]>([]);
  const [selectedProject, setSelectedProject] =
    useState<JiraProjectDisplay | null>(null);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);

  // ✅ Changed from Set to single string (or null)
  const [pinnedProjectId, setPinnedProjectId] = useState<string | null>(null);
  const [hoveredProject, setHoveredProject] = useState<string | null>(null);

  // State for user list view
  const [showUserList, setShowUserList] = useState(false);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [users, setUsers] = useState<JiraProjectUser[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ✅ REMOVED: Local toast state (moved to parent)
  // const [showToast, setShowToast] = useState(false);
  // const [toastType, setToastType] = useState<'success' | 'error'>('success');
  // const [toastTitle, setToastTitle] = useState('');
  // const [toastMessage, setToastMessage] = useState('');

  // ✅ Load pinned project from localStorage on mount
  useEffect(() => {
    const savedPinnedId = localStorage.getItem(PINNED_PROJECT_KEY);
    if (savedPinnedId) {
      setPinnedProjectId(savedPinnedId);
      console.log("📌 Loaded pinned project from localStorage:", savedPinnedId);
    }
  }, []);

  // Check Jira connection status when popup opens
  useEffect(() => {
    const checkConnection = async () => {
      if (isOpen) {
        setIsCheckingConnection(true);
        try {
          const status = await JiraIntegrationService.checkIntegrationStatus();
          setIsConnected(status.isConnected);

          // If connected, load projects
          if (status.isConnected) {
            setIsLoadingProjects(true);
            const jiraProjects = await JiraIntegrationService.getProjects();

            // Cast to API structure and map to display format
            const projectsFromAPI =
              jiraProjects as unknown as JiraProjectFromAPI[];
            setProjects(
              projectsFromAPI.map((p) => ({
                id: p.id,
                key: p.key,
                name: p.name,
                avatarUrl:
                  p.avatarUrls["48x48"] ||
                  p.avatarUrls["32x32"] ||
                  p.avatarUrls["24x24"],
              })),
            );
          }
        } catch (error) {
          console.error("Error checking Jira connection:", error);
          setIsConnected(false);
        } finally {
          setIsCheckingConnection(false);
          setIsLoadingProjects(false);
        }
      }
    };

    checkConnection();
  }, [isOpen]);

  // ✅ Auto-select pinned project when popup opens
  useEffect(() => {
    if (isOpen && pinnedProjectId && projects.length > 0) {
      const pinnedProject = projects.find((p) => p.id === pinnedProjectId);
      if (pinnedProject) {
        setSelectedProject(pinnedProject);
        console.log("✅ Auto-selected pinned project:", pinnedProject.name);
      }
    }
  }, [isOpen, pinnedProjectId, projects]);

  // Animation handling
  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => {
        setIsAnimating(true);
      });
    } else if (isAnimating) {
      const timer = setTimeout(() => {
        setIsAnimating(false);
        setSearchQuery("");
        // ✅ Don't clear selectedProject - keep it for next open
        setShowUserList(false);
        setSelectedUser(null);
        setUserSearchQuery("");
        setUsers([]);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isOpen, isAnimating]);

  const handleConnectClick = () => {
    onClose();
    navigate("/dashboard/settings/integration");
  };

  const handleProjectSelect = (project: JiraProjectDisplay) => {
    setSelectedProject(project);
  };

  // Handle double-click to show user list
  const handleProjectDoubleClick = async (project: JiraProjectDisplay) => {
    setSelectedProject(project);
    setShowUserList(true);

    // Fetch users for this project
    setIsLoadingUsers(true);
    try {
      const projectUsers = await JiraIntegrationService.getProjectUsers(
        project.key,
      );
      setUsers(projectUsers);
    } catch (error) {
      console.error("Error fetching users for project:", error);
      setUsers([]);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  // Handle back navigation from user list
  const handleBackToProjects = () => {
    setShowUserList(false);
    setUserSearchQuery("");
    setSelectedUser(null);
    setUsers([]);
  };

  // Handle user selection
  const handleUserSelect = (accountId: string) => {
    setSelectedUser(accountId);
  };

  // ✅ Modified to handle single pin with localStorage persistence
  const handleTogglePin = (e: React.MouseEvent, projectId: string) => {
    e.stopPropagation();

    if (pinnedProjectId === projectId) {
      // Unpin if clicking on already pinned project
      setPinnedProjectId(null);
      localStorage.removeItem(PINNED_PROJECT_KEY);
      console.log("📌 Unpinned project:", projectId);
    } else {
      // Pin new project (replaces any existing pin)
      setPinnedProjectId(projectId);
      localStorage.setItem(PINNED_PROJECT_KEY, projectId);
      console.log("📌 Pinned project:", projectId);
    }
  };

  const handleAddToJira = async () => {
    if (!selectedProject) return;

    // Validate feedbackId
    if (!feedbackId) {
      console.error("❌ No feedback ID provided");
      onJiraActionComplete?.({
        type: "error",
        title: "Unable to Add to Jira",
        message: "No feedback ID provided",
      });
      onClose();
      return;
    }

    // Convert feedbackId to string first, then parse
    const feedbackIdStr = String(feedbackId);
    const feedbackIdNumber = parseInt(feedbackIdStr, 10);

    if (isNaN(feedbackIdNumber)) {
      console.error("❌ Invalid feedback ID:", feedbackId);
      onJiraActionComplete?.({
        type: "error",
        title: "Invalid Feedback ID",
        message: "Unable to add to Jira: Invalid feedback ID",
      });
      onClose();
      return;
    }

    setIsSubmitting(true);
    try {
      console.log("🚀 Adding feedback to Jira project:", {
        feedbackId: feedbackIdNumber,
        project: selectedProject,
        assignee: selectedUser,
      });

      // Use the appropriate API based on whether a user is selected
      let result;
      if (selectedUser) {
        console.log("👤 Assigning to user:", selectedUser);
        result = await JiraIntegrationService.pushFeedbackToCustom(
          feedbackIdNumber,
          selectedProject.key,
          selectedUser,
        );
      } else {
        result = await JiraIntegrationService.pushFeedbackToCustom(
          feedbackIdNumber,
          selectedProject.key,
        );
      }

      if (result.success) {
        console.log("✅ Successfully created Jira issue:", {
          issueKey: result.jiraIssueKey,
          project: result.project,
          assignee: result.assigneeAccountId,
          status: result.finalStatus,
          message: result.message,
        });

        onJiraActionComplete?.({
          type: "success",
          title: "Success!",
          message: `Jira issue ${result.jiraIssueKey} created in ${result.project}`,
          jiraIssueKey: result.jiraIssueKey,
        });
      } else {
        console.error("❌ Failed to create Jira issue:", result.error);
        onJiraActionComplete?.({
          type: "error",
          title: "Failed to Create Jira Issue",
          message: result.error || "An error occurred",
        });
      }
    } catch (error) {
      console.error("❌ Error adding to Jira:", error);
      onJiraActionComplete?.({
        type: "error",
        title: "Error",
        message: "An unexpected error occurred while creating the Jira issue",
      });
    } finally {
      setIsSubmitting(false);
    }

    onClose();
  };

  const filteredProjects = projects.filter(
    (project) =>
      project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.key.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  // ✅ Sort projects: pinned first, then alphabetically
  const sortedProjects = [...filteredProjects].sort((a, b) => {
    const aIsPinned = pinnedProjectId === a.id;
    const bIsPinned = pinnedProjectId === b.id;

    if (aIsPinned && !bIsPinned) return -1;
    if (!aIsPinned && bIsPinned) return 1;
    return a.name.localeCompare(b.name);
  });

  // Filter users based on search query
  const filteredUsers = users.filter(
    (user) =>
      user.name.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
      (user.email &&
        user.email.toLowerCase().includes(userSearchQuery.toLowerCase())),
  );

  // Helper function to get initials from name
  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((word) => word.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  // Helper function to generate avatar color based on name
  const getAvatarColor = (name: string) => {
    const colors = [
      "bg-purple-500",
      "bg-green-500",
      "bg-blue-500",
      "bg-red-500",
      "bg-pink-500",
      "bg-indigo-500",
      "bg-yellow-500",
      "bg-teal-500",
      "bg-orange-500",
      "bg-cyan-500",
    ];
    const index = name
      .split("")
      .reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[index % colors.length];
  };

  if (!isOpen && !isAnimating) return null;

  return (
    <>
      {/* ✅ REMOVED: Toast component rendering - now handled by parent */}
      {/* {showToast && (
        <Toast
          type={toastType}
          title={toastTitle}
          message={toastMessage}
          onClose={() => setShowToast(false)}
        />
      )} */}


      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black z-40 transition-opacity duration-500 ease-in-out ${
          isOpen ? "bg-opacity-25" : "bg-opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
      />

      {/* Popup Panel */}
      <div
        className={`fixed right-0 top-0 h-full w-[420px] 3xl:w-[538px] bg-white shadow-2xl z-50 transition-transform duration-500 ease-out flex flex-col ${
          isOpen && isAnimating ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header - Only show when connected */}
        {isConnected && !isCheckingConnection && (
          <div className="flex items-center justify-between px-6 pt-5 pb-3 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <h2
                className="text-lg 3xl:text-xl font-semibold text-[#1E1F26]"
                style={{ fontFamily: DEFAULT_FONT_FAMILY }}
              >
                Add to Jira
              </h2>
              <img
                src={jiraIcon}
                alt="Jira"
                className="w-5 h-5 3xl:w-6 3xl:h-6"
              />
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        )}

        {/* Content */}
        {isCheckingConnection ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin h-8 w-8 border-4 border-black border-t-transparent rounded-full"></div>
          </div>
        ) : !isConnected ? (
          <div className="flex flex-col items-center justify-center h-full px-6">
            {/* Close button for not connected state */}
            <button
              onClick={onClose}
              className="absolute top-5 right-6 p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>

            <div className="flex items-center justify-center mb-6">
              <img
                src={CluvaJiraIntegration}
                alt="Jira"
                className="w-[250px] h-[36px] 3xl:w-[280px] 3xl:h-[40px]"
              />
            </div>
            <p
              className="text-sm 3xl:text-base text-[#1E1F26] text-center mb-8 max-w-sm"
              style={{ fontFamily: DEFAULT_FONT_FAMILY }}
            >
              Add Cluva feedback to Jira ticket instantly!
            </p>
            <Button onClick={handleConnectClick}>Connect Jira</Button>
          </div>
        ) : (
          <div className="flex flex-col flex-1 overflow-hidden">
            {/* Back button and Search Bar */}
            <div className="px-6 pt-4 pb-3">
              {showUserList && (
                <button
                  onClick={handleBackToProjects}
                  className="flex items-center gap-2 mb-3 text-base 3xl:text-lg text-[#1E1F26] "
                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                >
                  <span>←</span>
                  <span
                    className="font-medium"
                    style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                  >
                    {selectedProject?.name}
                  </span>
                </button>
              )}
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder={
                    showUserList
                      ? "Search user from project"
                      : "Search project from your workspace"
                  }
                  value={showUserList ? userSearchQuery : searchQuery}
                  onChange={(e) =>
                    showUserList
                      ? setUserSearchQuery(e.target.value)
                      : setSearchQuery(e.target.value)
                  }
                  className="w-full pl-10 pr-4 py-2.5 text-sm 3xl:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                />
              </div>
            </div>

            {/* SELECT PROJECT/USER Label */}
            <div className="px-6 py-2">
              <p
                className="text-xs 3xl:text-sm text-[#898D9C] font-medium uppercase tracking-wider"
                style={{ fontFamily: DEFAULT_FONT_FAMILY }}
              >
                {showUserList ? "SELECT USER TO ASSIGN" : "SELECT PROJECT"}
              </p>
            </div>

            {/* Combined Project/User List */}
            <div className="flex-1 relative overflow-hidden">
              {/* Scrollable List */}
              <div className="absolute inset-0 overflow-y-auto pl-6 pr-4 custom-scrollbar-overlay">
                {isLoadingProjects ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin h-6 w-6 border-3 border-black border-t-transparent rounded-full"></div>
                  </div>
                ) : showUserList ? (
                  // User List
                  isLoadingUsers ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin h-6 w-6 border-3 border-black border-t-transparent rounded-full"></div>
                    </div>
                  ) : filteredUsers.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <p className="text-sm 3xl:text-base">No users found</p>
                    </div>
                  ) : (
                    <div className="space-y-2 pb-6">
                      {filteredUsers.map((user) => (
                        <div
                          key={user.account_id}
                          onClick={() => handleUserSelect(user.account_id)}
                          className={`flex items-center gap-3 p-2.5 rounded-[6px] cursor-pointer ${
                            selectedUser === user.account_id
                              ? "bg-[#F8F9FC] border-[0.5px] border-[#1E1F26]"
                              : "bg-white hover:bg-[#F8F9FC]"
                          }`}
                        >
                          <div
                            className={`flex-shrink-0 w-7 h-7 3xl:w-8 3xl:h-8 rounded ${getAvatarColor(user.name)} flex items-center justify-center`}
                          >
                            <span className="text-white font-semibold text-sm 3xl:text-base">
                              {getInitials(user.name)}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3
                              className="text-sm 3xl:text-base font-medium text-[#1E1F26]"
                              style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                            >
                              {user.name}
                            </h3>
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                ) : filteredProjects.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p className="text-sm 3xl:text-base">No projects found</p>
                  </div>
                ) : (
                  <div className="space-y-2 pb-20">
                    {sortedProjects.map((project) => (
                      <div
                        key={project.id}
                        onClick={() => handleProjectSelect(project)}
                        onDoubleClick={() => handleProjectDoubleClick(project)}
                        onMouseEnter={() => setHoveredProject(project.id)}
                        onMouseLeave={() => setHoveredProject(null)}
                        className={`flex items-center gap-3 p-2.5 rounded-[6px] cursor-pointer relative ${
                          selectedProject?.id === project.id
                            ? "bg-[#F8F9FC] border-[0.5px] border-[#1E1F26]"
                            : "bg-white hover:bg-[#F8F9FC]"
                        }`}
                      >
                        <ProjectAvatar project={project} />

                        <div className="flex-1 min-w-0">
                          <h3
                            className="text-sm 3xl:text-base font-medium text-[#1E1F26]"
                            style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                          >
                            {project.name}
                          </h3>
                        </div>

                        {/* ✅ Single pin icon - show when hovered OR when pinned */}
                        {(hoveredProject === project.id ||
                          pinnedProjectId === project.id) && (
                          <div
                            onClick={(e) => handleTogglePin(e, project.id)}
                            className="flex-shrink-0 flex items-center justify-center cursor-pointer"
                          >
                            {pinnedProjectId === project.id ? (
                              <PinSelectedIcon className="w-4 h-4 3xl:w-5 3xl:h-5" />
                            ) : (
                              <PinUnSelectedIcon className="w-4 h-4 3xl:w-5 3xl:h-5" />
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Pro Tip - Only show when on project list */}
              {!showUserList && (
                <div className="absolute bottom-0 left-0 right-2 px-6 py-3 pointer-events-none z-10">
                  <div
                    className="absolute inset-0 bg-white/80"
                    style={{
                      backdropFilter: "blur(4px)",
                      WebkitBackdropFilter: "blur(4px)",
                    }}
                  />
                  <div className="relative pointer-events-auto">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">💡</span>
                      <p
                        className="text-xs 3xl:text-sm text-[#1E1F26]"
                        style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                      >
                        <span className="font-medium">Pro tip:</span> Double
                        click on project to select user
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons - Dynamic text */}
            <div className="px-6 py-4 border-t border-gray-200 flex gap-3 bg-white">
              <Button
                variant="cancel"
                onClick={onClose}
                disabled={isSubmitting}
                className="flex-1 py-2.5 text-sm 3xl:text-base"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleAddToJira}
                disabled={!selectedProject || isSubmitting}
                className="flex-1 py-2.5 text-sm 3xl:text-base relative overflow-visible"
              >
                {isSubmitting ? (
                  <span
                    className="inline-block relative"
                    style={{ width: 40, height: 16 }}
                  >
                    <Lottie
                      animationData={DotLoader}
                      loop={true}
                      style={{
                        width: 120,
                        height: 48,
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                      }}
                    />
                  </span>
                ) : showUserList ? (
                  "Assign & add"
                ) : (
                  "Add"
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default JiraIntegrationPopup;
